/**
 * Client-side validation rules for evidence uploads. Single source of truth so
 * the uploader and the grid (or any other future consumer) stay in sync.
 *
 * Server enforces the same rules; this prevents the user from hitting the
 * network when we already know the file won't pass.
 */

export const EVIDENCE_MAX_BYTES = 25 * 1024 * 1024; // 25 MB
export const EVIDENCE_MAX_PER_SESSION = 20;

export const EVIDENCE_ALLOWED_MIME = Object.freeze([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
]);

export type EvidenceValidationError = 'tooLarge' | 'badType' | 'limitReached';

export const isImageEvidence = (mime: string | null | undefined): boolean => !!mime && mime.startsWith('image/');

export const validateEvidence = (file: File, currentCount: number): EvidenceValidationError | null => {
  if (currentCount >= EVIDENCE_MAX_PER_SESSION) return 'limitReached';
  if (file.size > EVIDENCE_MAX_BYTES) return 'tooLarge';
  if (!EVIDENCE_ALLOWED_MIME.includes(file.type)) return 'badType';
  return null;
};

export const formatFileSize = (bytes: number | null | undefined): string => {
  if (!bytes || bytes <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};
